/**
 * Pure gap-math for the "return visit" signal (pre-B2B gate PR-5, plan
 * `docs/plans/pre-b2b-gate-execution-2026-07-29.md` §5a). W1 return rate was
 * measured at ~3.8% with ZERO instrumentation of the return-visit stage
 * itself — this is the missing event. Kept pure (no `Date.now()`, no
 * `localStorage`) so the gap math is unit-testable without timers/mocks; the
 * localStorage read/write and `posthog.capture` call live in
 * `src/components/providers/posthog-provider.tsx`, which is the only caller.
 */

/** A visitor counts as "returning" once this many ms have elapsed since their
 * last recorded visit. 24h — matches the plan's stated threshold. */
export const RETURN_VISIT_THRESHOLD_MS = 24 * 60 * 60 * 1000;

/** Cap on the `days_since_last` property — an ancient/garbage timestamp (or a
 * visitor returning after months) must not blow out the property's range. */
export const MAX_DAYS_SINCE_LAST = 90;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whether this pageview should fire `return_visit`. `lastSeenMs` is whatever
 * was read from the `ld_last_seen_ts` localStorage key: absent/`null` means
 * this is the visitor's first-ever recorded visit on this device — that is
 * NOT a return visit, just a first visit, so it must return `false` (never
 * treat "no prior timestamp" as an infinite gap). A `lastSeenMs` in the
 * future (clock skew, tampered storage) is also not a return visit.
 */
export function shouldEmitReturnVisit(
	lastSeenMs: number | null | undefined,
	nowMs: number,
): boolean {
	if (lastSeenMs == null) return false;
	const gapMs = nowMs - lastSeenMs;
	if (gapMs <= 0) return false;
	return gapMs > RETURN_VISIT_THRESHOLD_MS;
}

/**
 * `days_since_last` property value: whole days, rounded, floored at 0 (never
 * negative even under clock skew) and capped at `MAX_DAYS_SINCE_LAST`.
 */
export function daysSinceLast(lastSeenMs: number, nowMs: number): number {
	const gapMs = Math.max(0, nowMs - lastSeenMs);
	const days = Math.round(gapMs / MS_PER_DAY);
	return Math.min(days, MAX_DAYS_SINCE_LAST);
}
